const TOKEN_KEY = 'dashboard_access_token';
const EMAIL_KEY = 'dashboard_admin_email';
const LEGACY_AUTH_KEY = 'isAuthenticated';
export const AUTH_CHANGED_EVENT = 'dashboard-auth-changed';

const FALLBACK_BACKEND_API = 'https://shopbackend.artstoheartsproject.com/api';

function notifyAuthChanged() {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event(AUTH_CHANGED_EVENT));
}

export function resolveApiBaseUrl() {
  const env = typeof import.meta !== 'undefined' && import.meta.env;
  const raw = (env && (env.VITE_API_BASE_URL || env.VITE_BACKEND_URL)) || '';
  const s = String(raw).trim().replace(/\/+$/, '');
  if (s && /^https:\/\//i.test(s)) {
    return s;
  }
  if (s && s.startsWith('/')) {
    // relative /api → use same host only in local vite proxy
    if (typeof window !== 'undefined' && /localhost|127\.0\.0\.1/.test(window.location.hostname)) {
      return `${window.location.origin}${s}`.replace(/\/+$/, '');
    }
  }
  return FALLBACK_BACKEND_API;
}

export function getAccessToken() {
  try {
    return (localStorage.getItem(TOKEN_KEY) || '').trim() || null;
  } catch {
    return null;
  }
}

export function getAdminEmail() {
  try {
    return (localStorage.getItem(EMAIL_KEY) || '').trim() || null;
  } catch {
    return null;
  }
}

export function isAuthenticated() {
  return Boolean(getAccessToken());
}

export function setSession({ accessToken, email }) {
  localStorage.setItem(TOKEN_KEY, accessToken);
  if (email) localStorage.setItem(EMAIL_KEY, email);
  localStorage.removeItem(LEGACY_AUTH_KEY);
  notifyAuthChanged();
}

export function clearSession() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(EMAIL_KEY);
  localStorage.removeItem(LEGACY_AUTH_KEY);
  notifyAuthChanged();
}

/** Subscribe to login/logout so React UI (Navbar) updates without refresh. */
export function subscribeAuth(callback) {
  if (typeof window === 'undefined') return () => {};
  const handler = () => callback();
  window.addEventListener(AUTH_CHANGED_EVENT, handler);
  window.addEventListener('storage', handler);
  return () => {
    window.removeEventListener(AUTH_CHANGED_EVENT, handler);
    window.removeEventListener('storage', handler);
  };
}

export function authHeaders(extra = {}) {
  const headers = { ...extra };
  const token = getAccessToken();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
}

export async function login(username, password) {
  const base = resolveApiBaseUrl();
  const response = await fetch(`${base}/auth/login/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok || !data?.access_token) {
    throw new Error(data?.error || `Login failed (${response.status})`);
  }
  setSession({
    accessToken: data.access_token,
    email: data.admin_email || data.email || username,
  });
  return data;
}

export async function verifySession() {
  const token = getAccessToken();
  if (!token) return false;
  try {
    const base = resolveApiBaseUrl();
    const response = await fetch(`${base}/admin/auth/me/`, {
      method: 'GET',
      headers: authHeaders({ 'Content-Type': 'application/json' }),
    });
    if (!response.ok) {
      clearSession();
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

export function logout() {
  clearSession();
}

/**
 * fetch wrapper that attaches Bearer token and redirects to /login on 401.
 */
export async function authFetch(url, options = {}) {
  const headers = authHeaders({
    ...(options.headers || {}),
  });
  // Let browser set multipart boundary when body is FormData
  if (typeof FormData !== 'undefined' && options.body instanceof FormData) {
    delete headers['Content-Type'];
  } else if (!headers['Content-Type'] && options.body && typeof options.body === 'string') {
    headers['Content-Type'] = 'application/json';
  }

  const response = await fetch(url, { ...options, headers });
  if (response.status === 401 && typeof window !== 'undefined') {
    clearSession();
    if (!window.location.pathname.startsWith('/login')) {
      window.location.assign('/login');
    }
  }
  return response;
}

export default {
  resolveApiBaseUrl,
  getAccessToken,
  getAdminEmail,
  isAuthenticated,
  setSession,
  clearSession,
  authHeaders,
  login,
  verifySession,
  logout,
  authFetch,
};
